// Adaptive quality: frame-rate windows, tiers and browser hints.
import { renderer } from './renderer.js';
import { bloom, composer, tiltH, tiltV } from './post.js';
import { state } from './state.js';
import { gapStats, isFrameCap, stuckAtMinimum, TIERS, tierStep } from '../rules/quality.js';

export let perf, perfTick;

export function buildQuality() {
  perfTick = function perfTick() {
    const now = performance.now();
    if (perf.last) perf.gaps.push(now - perf.last);
    perf.last = now;
    if (!perf.t0) {
      perf.t0 = now;
      return;
    }
    perf.frames++;
    if (now - perf.t0 < 2000) return;
    const fps = (perf.frames * 1000) / (now - perf.t0),
      { sd } = gapStats(perf.gaps);
    perf.frames = 0;
    perf.t0 = now;
    perf.gaps = [];
    // skip windows while the tab is hidden, during loading, or right after a change
    if (document.hidden || document.getElementById('splash') || perf.settle-- > 0) return;
    if (isFrameCap(fps, sd)) {
      showPerfToast('cap', false);
      perf.slow = 0;
      return;
    }
    const step = tierStep(perf, fps);
    if (step) applyTier(perf.tier + step, step > 0);
    if (stuckAtMinimum(perf, fps)) showPerfToast(softwareGPU ? 'hw' : 'slow', false);
  };
  // ---------- adaptive quality ----------
  // The frame rate is measured in 2 s windows. Below ~42 fps twice in a row the quality steps down (resolution, shadow
  // refresh, bloom, tilt-shift blur, crowd distance); well above 57 fps for a while it steps back up to where it started.
  // If the GPU is a software rasteriser, or the browser is capping the rate (power saving), the visitor gets a hint
  // about the browser setting to change, since no quality level fixes that.
  const COARSE = matchMedia('(pointer: coarse)').matches;
  perf = {
    tier: 0,
    startTier: 0,
    shadowEvery: 2,
    frames: 0,
    t0: 0,
    last: 0,
    slow: 0,
    fast: 0,
    verySlow: 0,
    settle: 0,
    gaps: [],
    warned: false,
  };
  const gpuName = (() => {
    try {
      const gl = renderer.getContext(),
        ext = gl.getExtension('WEBGL_debug_renderer_info');
      return ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    } catch (e) {
      return '';
    }
  })();
  const softwareGPU = /swiftshader|llvmpipe|softpipe|software|basic render/i.test(gpuName);
  function applyTier(t, announce) {
    perf.tier = t;
    const T = TIERS[t];
    const pr = Math.min(devicePixelRatio, COARSE ? Math.min(1, T.pr) : T.pr);
    renderer.setPixelRatio(pr);
    composer.setPixelRatio(pr);
    composer.setSize(innerWidth, innerHeight);
    perf.shadowEvery = T.shadowEvery;
    bloom.enabled = T.bloom;
    tiltH.enabled = tiltV.enabled = T.tilt;
    state.cullD2 = T.cull * T.cull;
    perf.settle = 3;
    perf.slow = perf.fast = 0;
    try {
      localStorage.setItem('quality', String(t));
    } catch (e) {}
    if (announce) showPerfToast('quality', true);
  }
  {
    let saved = NaN;
    try {
      saved = parseInt(localStorage.getItem('quality'), 10);
    } catch (e) {}
    perf.startTier = softwareGPU ? 3 : COARSE ? 1 : 0;
    applyTier(
      Number.isFinite(saved) ? Math.max(perf.startTier, Math.min(3, saved)) : perf.startTier,
      false,
    );
  }
  // which browser, for the right instructions
  const UA = navigator.userAgent,
    BROWSER = /Edg\//.test(UA)
      ? 'edge'
      : /Firefox\//.test(UA)
        ? 'firefox'
        : /Chrome\//.test(UA)
          ? 'chrome'
          : /Safari\//.test(UA)
            ? 'safari'
            : 'other';
  const PERF_TXT = {
    es: {
      quality: 'Hemos bajado un poco la calidad gráfica para que vaya fluido en tu equipo.',
      hw: {
        chrome:
          'Tu navegador está dibujando el 3D sin la tarjeta gráfica. Activa <b>Configuración › Sistema › Usar aceleración por hardware cuando esté disponible</b> y reinicia el navegador.',
        edge: 'Tu navegador está dibujando el 3D sin la tarjeta gráfica. Activa <b>Configuración › Sistema y rendimiento › Usar aceleración de gráficos cuando esté disponible</b> y reinicia Edge.',
        firefox:
          'Firefox está dibujando el 3D sin la tarjeta gráfica. En <b>Ajustes › General › Rendimiento</b> desmarca la configuración recomendada y activa <b>Usar aceleración por hardware cuando esté disponible</b>.',
        safari:
          'Tu navegador está dibujando el 3D sin aceleración gráfica. Comprueba que no estás en Modo de bajo consumo y prueba a reiniciar Safari.',
        other:
          'Tu navegador está dibujando el 3D sin la tarjeta gráfica. Activa la aceleración por hardware en los ajustes del navegador y reinícialo.',
      },
      cap: {
        chrome:
          'El navegador parece limitar la animación a 30 fps. Si está activo, desactiva <b>Configuración › Rendimiento › Ahorro de energía</b> o conecta el portátil a la corriente.',
        edge: 'El navegador parece limitar la animación a 30 fps. Desactiva <b>Configuración › Sistema y rendimiento › Ahorro de energía</b> o conecta el portátil a la corriente.',
        firefox:
          'La animación parece limitada a 30 fps. Revisa el modo de ahorro de batería de tu sistema o conecta el portátil a la corriente.',
        safari:
          'La animación parece limitada a 30 fps. Desactiva el <b>Modo de bajo consumo</b> (Ajustes del Sistema › Batería, o Ajustes › Batería en el iPhone).',
        other:
          'La animación parece limitada a 30 fps. Desactiva el modo de ahorro de energía o conecta el dispositivo a la corriente.',
      },
      slow: 'Sigue yendo lento incluso con la calidad mínima. Cerrar otras pestañas, conectar el portátil a la corriente o activar la aceleración por hardware del navegador suele ayudar.',
      ok: 'Entendido',
    },
    en: {
      quality: 'We lowered the graphics quality a little so it runs smoothly on your device.',
      hw: {
        chrome:
          'Your browser is drawing the 3D without the graphics card. Turn on <b>Settings › System › Use graphics acceleration when available</b> and restart the browser.',
        edge: 'Your browser is drawing the 3D without the graphics card. Turn on <b>Settings › System and performance › Use graphics acceleration when available</b> and restart Edge.',
        firefox:
          'Firefox is drawing the 3D without the graphics card. In <b>Settings › General › Performance</b> untick the recommended settings and turn on <b>Use hardware acceleration when available</b>.',
        safari:
          'Your browser is drawing the 3D without graphics acceleration. Make sure Low Power Mode is off and try restarting Safari.',
        other:
          'Your browser is drawing the 3D without the graphics card. Turn on hardware acceleration in the browser settings and restart it.',
      },
      cap: {
        chrome:
          'The browser seems to cap the animation at 30 fps. If it is on, turn off <b>Settings › Performance › Energy saver</b> or plug your laptop in.',
        edge: 'The browser seems to cap the animation at 30 fps. Turn off <b>Settings › System and performance › Energy saver</b> or plug your laptop in.',
        firefox:
          'The animation seems capped at 30 fps. Check your system battery saver or plug your laptop in.',
        safari:
          'The animation seems capped at 30 fps. Turn off <b>Low Power Mode</b> (System Settings › Battery, or Settings › Battery on iPhone).',
        other:
          'The animation seems capped at 30 fps. Turn off power saving or plug your device in.',
      },
      slow: 'It is still slow even at the lowest quality. Closing other tabs, plugging your laptop in or enabling the browser’s hardware acceleration usually helps.',
      ok: 'Got it',
    },
  };
  const perfToast = document.getElementById('perfToast');
  function showPerfToast(kind, auto) {
    try {
      if (localStorage.getItem('perfToast:' + kind)) return;
    } catch (e) {}
    const T = PERF_TXT[state.lang],
      msg = typeof T[kind] === 'string' ? T[kind] : T[kind][BROWSER];
    perfToast.querySelector('.msg').innerHTML = msg;
    perfToast.querySelector('button').textContent = T.ok;
    perfToast.className = 'open ' + (auto ? 'info' : 'warn');
    clearTimeout(perfToast._t);
    if (auto) perfToast._t = setTimeout(() => perfToast.classList.remove('open'), 6000);
    perfToast.querySelector('button').onclick = () => {
      perfToast.classList.remove('open');
      if (!auto)
        try {
          localStorage.setItem('perfToast:' + kind, '1');
        } catch (e) {}
    };
  }
  if (softwareGPU) setTimeout(() => showPerfToast('hw', false), 3000);
  window.perfDebug = () => ({ tier: perf.tier, gpuName, softwareGPU, BROWSER });
}
